const STORAGE_KEY = "solarent_app_build_version";
const RELOAD_GUARD_KEY = "solarent_app_reload_guard";

const readEmbeddedVersion = () =>
  document.querySelector('meta[name="app-build-version"]')?.content || "";

const readStoredVersion = () => {
  try {
    return sessionStorage.getItem(STORAGE_KEY) || "";
  } catch {
    return "";
  }
};

const storeVersion = (version) => {
  try {
    if (version) {
      sessionStorage.setItem(STORAGE_KEY, version);
    }
  } catch {
    // ignore storage errors
  }
};

const readReloadGuard = () => {
  try {
    return sessionStorage.getItem(RELOAD_GUARD_KEY) || "";
  } catch {
    return "";
  }
};

const writeReloadGuard = (value) => {
  try {
    if (value) sessionStorage.setItem(RELOAD_GUARD_KEY, value);
    else sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {
    // ignore
  }
};

const reloadForNewVersion = (nextVersion) => {
  storeVersion(nextVersion);
  const url = new URL(window.location.href);
  url.searchParams.set("_v", nextVersion);
  window.location.replace(url.toString());
};

export const checkForAppUpdate = async () => {
  if (process.env.NODE_ENV !== "production") {
    return;
  }

  try {
    const response = await fetch(`/version.json?_=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-cache" },
    });

    if (!response.ok) {
      return;
    }

    const data = await response.json();
    const nextVersion = String(data?.version || data?.mainJs || "")
      .replace(/^.*main\./i, "")
      .replace(/\.js$/i, "")
      .trim();
    if (!nextVersion) {
      return;
    }

    const embeddedVersion = readEmbeddedVersion();
    const currentVersion = embeddedVersion || readStoredVersion() || "";

    if (!currentVersion) {
      storeVersion(nextVersion);
      writeReloadGuard("");
      return;
    }

    if (currentVersion === nextVersion) {
      storeVersion(nextVersion);
      writeReloadGuard("");
      return;
    }

    // Prevent infinite reload when version.json is stale/out of sync with index.html
    const transition = `${currentVersion}->${nextVersion}`;
    const urlVersion = new URL(window.location.href).searchParams.get("_v") || "";
    if (readReloadGuard() === transition || urlVersion === nextVersion) {
      storeVersion(embeddedVersion || currentVersion);
      writeReloadGuard("");
      return;
    }

    writeReloadGuard(transition);
    reloadForNewVersion(nextVersion);
  } catch {
    // network errors should not break the app
  }
};

export const startAppVersionMonitor = () => {
  if (process.env.NODE_ENV !== "production") {
    return undefined;
  }

  checkForAppUpdate();

  const onFocus = () => {
    checkForAppUpdate();
  };

  const onVisibility = () => {
    if (!document.hidden) {
      checkForAppUpdate();
    }
  };

  window.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onVisibility);

  const intervalId = window.setInterval(checkForAppUpdate, 5 * 60 * 1000);

  return () => {
    window.removeEventListener("focus", onFocus);
    document.removeEventListener("visibilitychange", onVisibility);
    window.clearInterval(intervalId);
  };
};
